import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, ArrowLeft, Trash2, Save, ExternalLink, GripVertical, Eye, EyeOff } from 'lucide-react';
import api from '../api';
import { extractErrorMessage } from '../lib/errors';
import RichTextEditor from '../components/RichTextEditor';

const DEFAULT_CATEGORIES = ['Getting Started', 'Campaigns', 'Contacts', 'Outlook Setup', 'Troubleshooting'];

export function AdminHelpList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/articles'); setItems(r.data); }
    catch (err) { toast.error(extractErrorMessage(err, 'Load failed')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const r = await api.post('/admin/articles', {
        title: 'New article', category: 'Getting Started',
        excerpt: '', body_html: '', published: false, order: items.length,
      });
      nav(`/app/admin/help/${r.data.id}`);
    } catch (err) { toast.error(extractErrorMessage(err, 'Create failed')); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this article? This cannot be undone.')) return;
    try { await api.delete(`/admin/articles/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(extractErrorMessage(err, 'Delete failed')); }
  };

  // Simple drag-to-reorder within a category. Server persists the new order.
  const [dragId, setDragId] = useState(null);
  const onDrop = async (targetId, category) => {
    if (!dragId || dragId === targetId) return;
    const inCat = items.filter(i => i.category === category).map(i => i.id);
    const from = inCat.indexOf(dragId);
    const to = inCat.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const reordered = [...inCat];
    reordered.splice(from, 1);
    reordered.splice(to, 0, dragId);
    // Optimistic UI
    setItems(list => {
      const others = list.filter(i => i.category !== category);
      const cat = reordered.map((id, idx) => ({ ...list.find(x => x.id === id), order: idx }));
      return [...others, ...cat];
    });
    try { await api.post('/admin/articles/reorder', { ids: reordered }); }
    catch (err) { toast.error(extractErrorMessage(err, 'Reorder failed')); load(); }
    setDragId(null);
  };

  const grouped = items.reduce((m, a) => {
    (m[a.category || 'Uncategorised'] ||= []).push(a);
    return m;
  }, {});

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl">Help articles</h1>
          <p className="text-zinc-500 mt-1">Admin-only. Draft, edit, reorder and publish walkthroughs for <Link to="/help" className="text-[#f5d67a] hover:underline">the public help center</Link>.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/help" target="_blank"><Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900"><ExternalLink size={14} className="mr-1"/>View public</Button></Link>
          <Button onClick={create} className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" data-testid="admin-new-article-btn">
            <Plus size={14} className="mr-1"/>New article
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="mt-8 bg-[#111110] border-zinc-900 p-12 text-center">
          <div className="font-display text-xl">No articles yet.</div>
          <p className="text-sm text-zinc-500 mt-2">Click <strong className="text-zinc-300">New article</strong> to write your first walkthrough.</p>
        </Card>
      ) : (
        <div className="mt-8 space-y-6">
          {Object.keys(grouped).sort().map(cat => (
            <div key={cat}>
              <div className="text-xs uppercase tracking-widest text-[#d4a94a] mb-2">{cat}</div>
              <div className="space-y-1">
                {grouped[cat].sort((a,b)=>a.order-b.order).map(a => (
                  <div
                    key={a.id}
                    draggable
                    onDragStart={()=>setDragId(a.id)}
                    onDragOver={(e)=>e.preventDefault()}
                    onDrop={()=>onDrop(a.id, cat)}
                    data-testid={`admin-article-row-${a.id}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-md border border-zinc-900 bg-[#111110] hover:border-[#3a2f18] transition-colors cursor-move"
                  >
                    <GripVertical size={14} className="text-zinc-600"/>
                    <div className="flex-1 min-w-0">
                      <Link to={`/app/admin/help/${a.id}`} className="text-sm text-zinc-100 truncate hover:text-[#f5d67a]">{a.title}</Link>
                      <div className="text-xs text-zinc-500 truncate">/help/{a.slug}</div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded ${a.published ? 'bg-emerald-950 text-emerald-300 border border-emerald-900' : 'bg-zinc-900 text-zinc-400 border border-zinc-800'}`}>
                      {a.published ? 'published' : 'draft'}
                    </span>
                    <button onClick={()=>del(a.id)} className="text-zinc-500 hover:text-red-400" data-testid={`admin-article-delete-${a.id}`}><Trash2 size={14}/></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminHelpEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [article, setArticle] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/articles/${id}`).then(r => setArticle(r.data))
      .catch(() => { toast.error('Article not found'); nav('/app/admin/help'); });
  }, [id, nav]);

  const save = async ({ silent } = {}) => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/articles/${id}`, {
        title: article.title,
        slug: article.slug,
        excerpt: article.excerpt,
        category: article.category,
        body_html: article.body_html,
        order: article.order || 0,
        published: !!article.published,
      });
      setArticle(a => ({ ...a, ...r.data }));
      if (!silent) toast.success('Saved');
    } catch (err) { toast.error(extractErrorMessage(err, 'Save failed')); }
    finally { setSaving(false); }
  };

  if (!article) return <div className="p-8 text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <Link to="/app/admin/help" className="text-sm text-zinc-500 hover:text-[#f5d67a] flex items-center gap-1"><ArrowLeft size={14}/>All articles</Link>
      <div className="mt-4 flex items-start justify-between gap-4">
        <Input
          value={article.title}
          onChange={e=>setArticle(a=>({...a, title: e.target.value}))}
          placeholder="Article title"
          className="bg-transparent border-none text-3xl font-display px-0 focus-visible:ring-0 h-auto text-zinc-100"
          data-testid="admin-article-title"
        />
        <div className="flex gap-2 shrink-0">
          <Link to={`/help/${article.slug}`} target="_blank">
            <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900"><ExternalLink size={14} className="mr-1"/>Preview</Button>
          </Link>
          <Button onClick={save} disabled={saving} className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" data-testid="admin-article-save">
            <Save size={14} className="mr-1"/>{saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-4">
          <div>
            <Label className="text-zinc-300">Excerpt</Label>
            <Input
              value={article.excerpt || ''}
              onChange={e=>setArticle(a=>({...a, excerpt: e.target.value}))}
              placeholder="One-line summary shown on the help index"
              className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"
              data-testid="admin-article-excerpt"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Body</Label>
            <div className="mt-1.5">
              <RichTextEditor value={article.body_html} onChange={html => setArticle(a => ({ ...a, body_html: html }))}/>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <Card className="bg-[#111110] border-zinc-900 p-4">
            <Label className="text-zinc-300">Category</Label>
            <Input
              list="cat-suggestions"
              value={article.category || ''}
              onChange={e=>setArticle(a=>({...a, category: e.target.value}))}
              className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"
              data-testid="admin-article-category"
            />
            <datalist id="cat-suggestions">
              {DEFAULT_CATEGORIES.map(c => <option key={c} value={c}/>)}
            </datalist>
          </Card>
          <Card className="bg-[#111110] border-zinc-900 p-4">
            <Label className="text-zinc-300">URL slug</Label>
            <Input
              value={article.slug || ''}
              onChange={e=>setArticle(a=>({...a, slug: e.target.value}))}
              className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a] font-mono text-sm"
              data-testid="admin-article-slug"
            />
            <div className="mt-1.5 text-[11px] text-zinc-500">Public URL: /help/{article.slug}</div>
          </Card>
          <Card className="bg-[#111110] border-zinc-900 p-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-zinc-300">Published</Label>
                <div className="text-[11px] text-zinc-500 mt-0.5">Drafts are hidden from the public help center.</div>
              </div>
              <Switch
                checked={!!article.published}
                onCheckedChange={(v)=>{ setArticle(a=>({...a, published: !!v})); }}
                data-testid="admin-article-published"
                className="data-[state=checked]:bg-[#d4a94a]"
              />
            </div>
            <div className="mt-3 text-[11px] text-zinc-500 flex items-center gap-1.5">
              {article.published ? <Eye size={12} className="text-emerald-400"/> : <EyeOff size={12}/>}
              {article.published ? 'Live at /help' : 'Only visible to you'}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
